import ModelVendors from "../../../../model/model-providers/modelVendors";
import "./ModelSelectionPanel.scss";
import LogoOpenAI from "../../../../assets/icons/LogoOpenAI";
import LogoAnthropic from "../../../../assets/icons/LogoAnthropic";
import LogoAmazon from "../../../../assets/icons/LogoAmazon";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { currentModelDescriptions } from "../../../../model/model-providers/modelDescriptors";
import LogoClaude from "../../../../assets/icons/LogoClaude";
import NebiusChatAvatar from "../details/NebiusChatAvatar";
import LogoEU from "../../../../assets/icons/LogoEU";
import { BotIcon } from "lucide-react";
import { loadAssistants } from "../../../workspace/assistants/store/assistantActionCreators";
import { assistantSelectors } from "../../../workspace/assistants/store/assistantReducer";
import Message from "../../../../i18n/Message";
import { loadVendorKeys } from "../../vendor-keys/store/vendorKeyActionCreators";
import { vendorKeySelectors } from "../../vendor-keys/store/vendorKeyReducer";
import label from "../../../../i18n/label";
import Tooltip from "../../../shared/Tooltip";

const ASSISTANTS_VENDOR = "ASSISTANTS";

const ModelSelectionPanel = ({
  modelId,
  setModelId,
  showAssistants = false,
  assistantUid,
  setAssistantUid,
}) => {
  const [selectedVendor, setSelectedVendor] = useState(ModelVendors.NEBIUS);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { workspaceId } = useParams();
  const assistantList = useSelector(assistantSelectors.getList);
  const listCounter = useSelector(assistantSelectors.getListCounter);
  const vendorKeys = useSelector(vendorKeySelectors.getList);
  const vendorKeysCounter = useSelector(vendorKeySelectors.getListCounter);

  // Load assistants if showAssistants is enabled
  useEffect(() => {
    if (showAssistants && workspaceId) {
      dispatch(loadAssistants(intl, workspaceId));
    }
  }, [showAssistants, dispatch, intl, workspaceId, listCounter]);

  // Load vendor keys to check which providers have API keys
  useEffect(() => {
    if (workspaceId) {
      dispatch(loadVendorKeys(intl, workspaceId));
    }
  }, [dispatch, intl, workspaceId, vendorKeysCounter]);

  // Helper function to check if vendor has API key
  const hasVendorKey = (vendor) => {
    if (!vendorKeys) return false;
    return vendorKeys.some((key) => key.vendor === vendor);
  };

  const onVendorChanged = (vendor, isDisabled) => {
    // Don't allow selection if no API key is configured (except for assistants)
    if (isDisabled || (vendor !== ASSISTANTS_VENDOR && !hasVendorKey(vendor))) {
      return;
    }
    setSelectedVendor(vendor);
    setModelId(null);
    if (setAssistantUid) {
      setAssistantUid(null);
    }
  };

  const onAssistantSelected = (uid) => {
    if (setAssistantUid) {
      setAssistantUid(uid);
    }
    setModelId(null);
  };

  const onModelSelected = (id) => {
    setModelId(id);
    if (setAssistantUid) {
      setAssistantUid(null);
    }
  };

  return (
    <div className="model_selection_panel">
      <div className="vendor_selector">
        {showAssistants && (
          <div
            className={
              "vendor_selector_item" +
              (selectedVendor === ASSISTANTS_VENDOR ? " selected" : "")
            }
            onClick={() => onVendorChanged(ASSISTANTS_VENDOR)}
          >
            <div className="vendor_selector_item_logo">
              <BotIcon size={24} color="#344767" />
            </div>
            <div className="vendor_selector_item_name">
              <Message id="menu_agents" />
            </div>
          </div>
        )}
        <Tooltip
          content={
            !hasVendorKey(ModelVendors.NEBIUS)
              ? label(intl, "vendor_key_required")
              : null
          }
        >
          <div
            className={
              "vendor_selector_item" +
              (selectedVendor === ModelVendors.NEBIUS ? " selected" : "") +
              (!hasVendorKey(ModelVendors.NEBIUS) ? " disabled" : "")
            }
            onClick={() =>
              onVendorChanged(
                ModelVendors.NEBIUS,
                !hasVendorKey(ModelVendors.NEBIUS)
              )
            }
          >
            <div className="vendor_selector_item_logo">
              <LogoEU size={32} />
            </div>
            <div className="vendor_selector_item_name">Open Source</div>
          </div>
        </Tooltip>
        <Tooltip
          content={
            !hasVendorKey(ModelVendors.ANTHROPIC)
              ? label(intl, "vendor_key_required")
              : null
          }
        >
          <div
            className={
              "vendor_selector_item" +
              (selectedVendor === ModelVendors.ANTHROPIC ? " selected" : "") +
              (!hasVendorKey(ModelVendors.ANTHROPIC) ? " disabled" : "")
            }
            onClick={() =>
              onVendorChanged(
                ModelVendors.ANTHROPIC,
                !hasVendorKey(ModelVendors.ANTHROPIC)
              )
            }
          >
            <div className="vendor_selector_item_logo">
              <LogoAnthropic size={32} />
            </div>
            <div className="vendor_selector_item_name">Anthropic</div>
          </div>
        </Tooltip>
        <Tooltip
          content={
            !hasVendorKey(ModelVendors.OPENAI)
              ? label(intl, "vendor_key_required")
              : null
          }
        >
          <div
            className={
              "vendor_selector_item" +
              (selectedVendor === ModelVendors.OPENAI ? " selected" : "") +
              (!hasVendorKey(ModelVendors.OPENAI) ? " disabled" : "")
            }
            onClick={() =>
              onVendorChanged(
                ModelVendors.OPENAI,
                !hasVendorKey(ModelVendors.OPENAI)
              )
            }
          >
            <div className="vendor_selector_item_logo">
              <LogoOpenAI size={32} />
            </div>
            <div className="vendor_selector_item_name">OpenAI</div>
          </div>
        </Tooltip>
      </div>
      <div className="model_selector">
        {selectedVendor === ASSISTANTS_VENDOR && showAssistants
          ? assistantList?.map((assistant) => {
              return (
                <div
                  key={assistant.uid}
                  className={
                    "model_item " +
                    (assistantUid === assistant.uid ? " selected" : "")
                  }
                  onClick={() => onAssistantSelected(assistant.uid)}
                >
                  <div className="logo">
                    <BotIcon size={24} color="#344767" />
                  </div>
                  <div className="name_column">
                    <div className="name">{assistant.name}</div>
                    <div className="context">{assistant.description || ""}</div>
                  </div>
                </div>
              );
            })
          : currentModelDescriptions
              .filter((item) => item.vendor === selectedVendor)
              .map((item) => {
                const isDisabled = !hasVendorKey(selectedVendor);
                return (
                  <div
                    key={item.id}
                    className={
                      "model_item " +
                      (modelId === item.id ? " selected" : "") +
                      (isDisabled ? " disabled" : "")
                    }
                    onClick={() => !isDisabled && onModelSelected(item.id)}
                  >
                    <div className="logo">
                      {item.vendor === ModelVendors.OPENAI && (
                        <LogoOpenAI size={32} />
                      )}
                      {item.vendor === ModelVendors.ANTHROPIC && (
                        <LogoClaude size={32} />
                      )}
                      {item.vendor === ModelVendors.AMAZON && (
                        <LogoAmazon size={32} />
                      )}
                      {item.vendor === ModelVendors.NEBIUS && (
                        <NebiusChatAvatar modelId={item.id} size={26} />
                      )}
                    </div>
                    <div className="name_column">
                      <div className="name">{item.name}</div>
                      <div className="context">{item.contextWindow}</div>
                    </div>
                  </div>
                );
              })}
      </div>
    </div>
  );
};

export default ModelSelectionPanel;
